<template>

<Information />

<div class="bg-white my-8 mx-6">
    <!-- 動画アップロードボタンを右上に配置 -->
    <div class="pb-2 px-8 pt-12">
        <!-- エラーメッセージ -->
        <ErrorMessage :message="errorMessage" @clear-message="errorMessage = ''" />

        <!-- 文字起こし進捗アナウンス -->
        <div v-if="isTranscriptionBusy" class="alert-error items-center mb-4">
            <i class="fa-solid fa-triangle-exclamation text-alert"></i>
            <p>ただいま文字起こし機能が混み合っており、完了まで通常よりお時間がかかる可能性がございます。</p>
            <button type="button" class="alert-close link-styles--gray" @click="isShowTranscriptionError = false">
                <i class="fa-light fa-xmark w-6 h-6" title="Close"></i>
            </button>
        </div>
        <div class="flex flex-col gap-y-2 sm:flex-row justify-between">
            <h1 class="text-2xl font-bold">動画管理</h1>
            <button @click="showVideoUploadModal = true" class="btn-primary py-3 self-end">
                動画をアップロード
            </button>
        </div>
    </div>
    <!-- ビデオデータの表表示 -->
    <div v-if="videos.length > 0" class="mt-6">
        <div class="overflow-x-auto px-8 pb-12">
            <table class="w-full">
                <thead class="text-sm border-b whitespace-nowrap">
                    <tr class="[&>th]:py-3 [&>th]:px-2 [&>th]:min-w-36 text-sub">
                        <th>動画</th>
                        <th>文字起こし進捗</th>
                        <th>記事公開</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="video in videos" :key="video.id" class="[&>td]:py-4 [&>td]:px-3 bg-white border-b">
                        <td class="min-w-40">
                            <div class="flex flex-wrap flex-col md:flex-row gap-2 items-start md:items-center">
                                <div class="shrink-0 w-full md:w-40 max-w-40">
                                    <div class="aspect-[16/9] w-full">
                                        <img class="w-full h-full object-cover rounded" :src="video.thumbnail" alt="サムネイル" />
                                    </div>
                                </div>
                                <div class="flex-1 md:min-w-20 line-clamp-2 break-all">
                                    {{ video.video_title }}
                                </div>
                            </div>
                        </td>
                        <td class="whitespace-nowrap text-center text-sm">
                            <!-- ステータスが100%の場合、ボタンを表示 -->
                            <div v-if="video.transcription_progress_display === '100%'">
                                <p class="mb-2">文字起こし完了</p>
                                <div v-if="canArticleUpdate">
                                    <button @click="goToArticleSettings(video.article_id)" class="btn-secondary btn-sm">
                                        記事公開設定へ
                                    </button>
                                </div>
                            </div>
                            <!-- 文字起こし進捗が0%の場合のボタン表示 -->
                            <div v-else-if="video.transcription_progress_display === '0%'">
                                <button @click="handleEditVideoInfo" :value="video.video_id" class="bg-red-500 hover:bg-red-700 text-white font-bold text-xs py-2 px-4 rounded">
                                    動画情報の修正
                                </button>
                            </div>
                            <!-- それ以外の場合、ステータスの値を表示 -->
                            <div v-else>
                                {{ video.transcription_progress_display }}
                            </div>
                        </td>
                        <td class="whitespace-nowrap text-center text-sm">{{ video.status_transcription_display }}</td>
                    </tr>
                </tbody>
            </table>
            <BasePaginator :data="videos" :total="total" :page="page" :pageStartNum="pageStartNum" :pageEndNum="pageEndNum" :hasNext="hasNext" :hasPrevious="hasPrevious" :fetchFunction="fetchVideos" @page-changed="fetchVideos" />
        </div>
    </div>
    <div v-else-if="isLoading" class="py-4">
        <DataLoading />
    </div>
    <div v-else class="text-center pt-6 pb-12 px-8">
        <img :src=notuploadedvideoImg alt="アイキャッチ" class="w-48 h-48 m-auto">
        <p class="text-sm py-8">アップロードされた動画はありません。<br>「<button @click="showVideoUploadModal = true" class="link-styles">動画をアップロードする</button>」から動画のアップロードを開始してください。</p>
    </div>

    <!-- ガイドラインが未同意時のポップアップウィンドウ -->
    <div v-if="isGuidelineOpen" class="fixed inset-0 bg-gray-500 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
      <div class="popup bg-white rounded-lg w-full max-w-4xl min-w-xs max-h-[698px] mx-[8%] p-8 gap-3 border-[1px] border-solid relative" v-scroll-lock="isGuidelineOpen" @click.stop>
        <div class="mb-8 flex flex-col items-center justify-center">
            <span class="dli-exclamation-circle mb-4"><span></span></span>
            <h2 class="guidelineTitle text-xl font-bold break-words">livedoor ECHOESご利用ガイドライン</h2>
        </div>
        <div class="gap-2 text-xs mb-4">
            <h3 class="text-sm font-bold mb-2">ガイドラインが新たに作成されました。</h3>
            <p class="mb-2">
              ご登録されるには当サービスの「ガイドライン」をご確認後、下記「ガイドラインに同意する」ボタンにてご同意ください。
            </p>
        </div>
        <UserGuidelineBody class="max-w-[832px] max-h-[358px] mb-4 p-4 border-gray-300 border-[1px] border-solid overflow-y-scroll"/>
        <button @click="contentUpdate" class="bg-emerald-600 rounded text-white w-full max-w-[832px] h-full max-h-[48px] py-2 px-3 font-bold">同意する</button>
      </div>
    </div>
    <VideoUploadModal
        v-if="showVideoUploadModal"
        :selectedVideo ="currentVideo"
        @close-modal="handleCloseVideoUploadModal"
    />
</div>
</template>

<script>
import {
    ref,
    computed,
    onMounted,
} from 'vue';
import axios from 'axios';
import notuploadedvideoImg from '@/assets/notuploadedvideo.png';
import uploadIcn from '@/assets/uploadIcn.svg';
import UserGuidelineBody from './UserGuidelineBody.vue';
import Information from './InformationTable.vue';
import DataLoading from './DataLoading.vue';
import BasePaginator from './BasePaginator.vue';
import ErrorMessage from './ErrorMessage';
import VideoUploadModal from './VideoUploadModal/VideoUploadModal.vue';
import {
    useStore
} from 'vuex';
import {
    useRouter
} from 'vue-router';

export default {
    name: 'VideoManagement',
    components: {
        UserGuidelineBody,
        Information,
        DataLoading,
        BasePaginator,
        ErrorMessage,
        VideoUploadModal,
    },
    setup() {
        const store = useStore();
        const router = useRouter();
        const videos = ref([]);
        const errorMessage = ref('');
        const currentVideo = ref(null);
        const showVideoUploadModal = ref(false);
        let isGuidelineOpen = ref(false);
        let canArticleUpdate = ref(true);
        let isLoading = ref(true);
        let isShowTranscriptionError = ref(true);

        let page = ref(1);
        let per_page = ref(20);
        let total = ref(0);
        let pageStartNum = ref(1);
        let pageEndNum = ref(20);
        let hasNext = ref(false);
        let hasPrevious = ref(false);

        // ビデオデータの取得
        const fetchVideos = async (pageNum=1) => {
            page.value = pageNum;
            if(page.value < 1) {
              page.value = 1
            }
            try {
                const accessToken = store.state.accessToken;
                const apiEndpoint = process.env.VUE_APP_API_ENDPOINT;
                const response = await axios.get(`${apiEndpoint}/videos/?page=${page.value}`, {
                    headers: {
                        'Authorization': `Bearer ${accessToken}`
                    }
                });
                videos.value = response.data.results;
                hasNext.value = response.data.hasNext;
                hasPrevious.value = response.data.hasPrevious;
                total.value = response.data.total;
                pageStartNum.value = response.data.start;
                pageEndNum.value = response.data.end;
                isLoading.value = false;
            } catch (error) {
                errorMessage.value = 'ビデオデータの取得中にエラーが発生しました。画面の再読み込みを行ってください。';
                console.error("ビデオデータの取得中にエラーが発生しました。", error);
            }

            // ガイドライン同意判定
            let shouldShowGuideline = false;
            const guidelineup = process.env.VUE_APP_GUIDELINE_UPDATE;

            try {
                const storedGuideline = store.state.guideline;
                if (storedGuideline) {
                    const jsonStr = JSON.parse(storedGuideline);
                    shouldShowGuideline = jsonStr.guideline !== guidelineup;
                } else {
                    shouldShowGuideline = true; // ガイドライン情報がない場合は表示
                }
            } catch (e) {
                console.error("ガイドライン情報の解析に失敗:", e);
                shouldShowGuideline = true; // エラーが出た場合も表示する
            }

            isGuidelineOpen.value = shouldShowGuideline;

            if (isGuidelineOpen.value) {
                document.documentElement.style.setProperty('overflow-y', 'hidden', 'important');
            }

            // 記事編集ON/OFF判定
            const articleUpdateFlg = process.env.VUE_APP_NEWS_UPDATE_ONOFF;
            if (articleUpdateFlg == 'OFF') {
                canArticleUpdate.value = false;
            }
        };

        onMounted(fetchVideos);

        const handleEditVideoInfo = (e) => {
            const videoId = parseInt(e.target.value);
            currentVideo.value = videos.value.find(video => video.video_id === videoId) || null;
            showVideoUploadModal.value = true;
        }

        // 進捗が混雑しているかどうかの判定
        const isTranscriptionBusy = computed(() => {
            const transcriptionInProgress = videos.value.filter(video => {
                const progress = parseFloat(video.transcription_progress_display);
                return progress > 0 && progress < 100;
            }).length;
            return transcriptionInProgress >= 10 && isShowTranscriptionError.value;
        });

        const goToArticleSettings = (articleId) => {
            router.push({
                name: 'ArticleSettings',
                params: {
                    id: articleId
                }
            });
        };

        // ガイドラインポップアップウィンドウで同意時の処理
        const contentUpdate = async () => {
            try {
                const apiEndpoint = process.env.VUE_APP_API_ENDPOINT;
                const guidelineup = process.env.VUE_APP_GUIDELINE_UPDATE;
                const payload = {
                    user_id: store.state.user_id,
                    guidelineup: guidelineup,
                    action: 'consent',
                };
                const response = await axios.post(`${apiEndpoint}/user-auth/`, payload, {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                });
                if (response.status === 200) {
                    // 受け取ったuserConsentをそのまま保存せず、構造化された形で保存
                    const userConsent = {"guideline": guidelineup};
                    store.commit('setGuidelineConsent', JSON.stringify(userConsent));
                    console.log('User Guideline consent successfully', userConsent);
                }
            } catch (error) {
                errorMessage.value = 'ユーザーの同意に失敗しました。';
                console.error("ユーザーの同意中にエラーが発生しました。", error);
            }
            isGuidelineOpen.value = false;
            document.documentElement.style.removeProperty('overflow-y');
        };

        const handleCloseVideoUploadModal = () => {
            showVideoUploadModal.value = false;
            currentVideo.value = null;
            fetchVideos();
        };

        return {
            videos,
            errorMessage,
            isTranscriptionBusy,
            showVideoUploadModal,
            handleEditVideoInfo,
            handleCloseVideoUploadModal,
            notuploadedvideoImg,
            fetchVideos, // fetchVideos関数をreturnする
            goToArticleSettings,
            uploadIcn,
            isGuidelineOpen, // ガイドラインポップアップ制御
            canArticleUpdate, // 記事編集ON/OFF
            contentUpdate,
            isLoading,
            page,
            per_page,
            total,
            pageStartNum,
            pageEndNum,
            hasNext,
            hasPrevious,
            currentVideo,
            isShowTranscriptionError,
        };
    }
}
</script>
<style scoped>
.group:hover .tooltip {
    opacity: 1;
}

/* ポップアップウィンドウのスタイル */
.popup {
  font-family: Hiragino Kaku Gothic ProN;
  border-color: #d1d5db;
}
.guidelineTitle {
  line-height: 30px;
  inline-size: 185px;
}
/* エクスクラメーションマーク(丸囲み)のスタイル */
.dli-exclamation-circle {
  display: inline-block;
  vertical-align: middle;
  color: white;
  background-color: blue;
  line-height: 1;
  position: relative;
  width: 1.5em;
  height: 1.5em;
  border: 0.1em solid currentColor;
  border-radius: 50%;
  box-sizing: content-box;
}
.dli-exclamation-circle > span {
  width: 0.15em;
  height: 0.6em;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  margin: auto;
}
.dli-exclamation-circle > span::before, .dli-exclamation-circle > span::after {
  content: '';
  display: block;
  width: inherit;
  background: currentColor;
}
.dli-exclamation-circle > span::before {
  margin-bottom: 0.1em;
  height: 0.35em;
  border-radius: 0.025em;
}
.dli-exclamation-circle > span::after {
  height: 0.15em;
  border-radius: 50%;
}
.bg-white shadow {
  display:none;
}
</style>
